import _ from "lodash";
import React, { useState, useEffect, useMemo, useRef } from "react";

// components
import PromptCards from "../PromptCards";
import AITutorChatWrapper from "AppComponents/AITutorChatWrapper";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";

// design system
import { Modal, SideNavigation, Button } from "@toddle-edu/ds-web";
import { ChatIllustration, colors } from "@toddle-edu/ds-theme";

// modules
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getAIPromptsQuery } from "../../modules/LessonPlanningQueries";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";

// third party
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

// utils
import { scrollIntoView } from "Utils";
import {
  getAITutorSidebarOptions,
  getPromptsForSuggestionLibrary,
} from "../../modules/Utils";
import { wrapPlaceholderWithSpan } from "../PromptCards/PromptCards";
import { getPromptLabelsObj } from "Utils/AIUtils";

// scss
import classes from "./AITutorLibrary.scss";

const source = "LIBRARY";

const styles = {
  modalBodyStyle: {
    padding: "0px",
    display: "flex",
    overflow: "hidden",
  },
  aiTutorChatContainerStyle: {
    backgroundColor: colors.surfaceSubtle,
  },
  sideNavigationStyle: {
    overflowY: "auto",
  },
};

const AITutorLibrary = props => {
  const {
    t,
    onPromptSuggestionModalClose: onClose,
    prompts,
    flattenedPrompts,
    assessmentDetails,
    setSelectedAction,
    leId,
    gradeId,
    course,
    assessmentTemplate,
    options,
    initialOption = null,
    aiConversation,
    initialGroupType,
    isLoading,
    isData,
    i18n,
    learningCourseId,
  } = props;

  // ------------------ states -----------------------

  const [selectedSidebarItem, setSelectedSidebarItem] = useState(initialOption);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedChildPrompt, setSelectedChildPrompt] = useState(null);

  const promptsContainerRef = useRef(null);

  // ------------------ side effects -----------------------

  // when info icon is clicked on the PromptCard then open the Library with that option being pre-selected
  // here initialGroupType will be the prompt id

  useEffect(() => {
    if (initialGroupType) {
      const selectedPrompt = _.find(
        flattenedPrompts,
        ({ id }) => id === initialGroupType
      );

      const { tutorGoal, tutorSummary } = getPromptLabelsObj({
        t,
        i18n,
        ...(selectedPrompt?.metadata || {}),
        ...prompt,
      });

      // set the correct sidebar item
      const selectedSidebarItem = _.find(options, option => {
        const { children } = option || {};
        return _.includes(children, initialGroupType);
      })?.key;

      if (selectedSidebarItem) setSelectedSidebarItem(selectedSidebarItem);

      // scroll to that prompt
      if (selectedPrompt) setSelectedPromptLocal(selectedPrompt);

      const childPromptId = _.get(selectedPrompt, "children.0");
      const childPrompt = _.find(flattenedPrompts, { id: childPromptId });

      if (childPrompt) {
        const label = wrapPlaceholderWithSpan({ label: tutorGoal });

        setSelectedChildPrompt({
          ...childPrompt,
          label: label,
          displayText: label,
          summary: tutorSummary,
        });
      }
    }
  }, [initialGroupType, initialOption]);

  // scroll to the selected prompt when the component mounts
  useEffect(() => {
    if (isData && initialGroupType && !_.isEmpty(selectedPrompt)) {
      const id = `${source}_PROMPT_${initialGroupType}`;
      const element = document.getElementById(id);
      scrollIntoView({
        element,
        block: "center",
        inline: "center",
        scrollAfter: 100,
      });
    }
  }, [initialGroupType, isData]);

  // set first sidebar option as selected when the component mounts
  useEffect(() => {
    if (initialOption && !initialGroupType) {
      setSelectedSidebarItem(initialOption);
    }
  }, [initialOption, initialGroupType]);

  // reset scroll to top when the sidebar option is changed
  useEffect(() => {
    if (promptsContainerRef?.current) {
      promptsContainerRef.current.scrollTop = 0;
    }
  }, [selectedSidebarItem]);

  // ------------------ functions -----------------------

  // reset selected prompts on sidebar option change
  const onClickSideNavigationOption = key => {
    setSelectedSidebarItem(key);
    setSelectedPrompt(null);
    setSelectedChildPrompt(null);
  };

  // select prompt and when same prompt is selected then un-select prompt
  // also modify the children to contain actual object instead of just id

  const setSelectedPromptLocal = prompt => {
    if (prompt?.id === selectedPrompt?.id) {
      setSelectedPrompt(null);
    } else {
      const childPrompt = _.map(prompt?.children, id =>
        _.find(flattenedPrompts, { id })
      );

      const children =
        _.get(childPrompt, "0.action") === "draft_ai_tutor"
          ? [prompt]
          : childPrompt;

      setSelectedPrompt({
        ...prompt,
        children,
      });
    }
  };

  // on "Use this template" click show the user that the chat will be reset
  // if the user had selected "Do not show this modal again" then directly reset the chat without dialog box
  const onClickUseThisTemplate = () => {
    if (!_.isEmpty(selectedChildPrompt)) {
      setSelectedAction(selectedChildPrompt);
    }

    onClose();
  };

  // ------------------ variables -----------------------

  const selectedSidebarItemObj = _.find(
    prompts,
    ({ id }) => id === selectedSidebarItem
  );

  const childPrompts = _.compact(
    _.get(selectedSidebarItemObj, "children", []).map(promptId =>
      _.find(prompts, { id: promptId })
    )
  );

  // localized prompt label
  const { displayText: label } = getPromptLabelsObj({
    t,
    i18n,
    ...(_.find(flattenedPrompts, { id: selectedSidebarItem }) || {}),
  });

  const promptHasConversation = _.some(aiConversation, ({ node }) => {
    const { entitySubType } = node || {};
    const { children } = selectedPrompt || {};
    const action = _.first(children)?.action;

    return _.includes(entitySubType, action);
  });

  const shouldGeneratePreviewChat =
    !_.isEmpty(selectedPrompt) && !promptHasConversation;

  const showEmptyState = _.isEmpty(selectedPrompt) && !promptHasConversation;

  const emptyStateProps = useMemo(() => {
    return {
      dsVersion: "2.0",
      illustration: ChatIllustration,
      size: "medium",
      title: t("ai:select_a_template_to_play_with_it"),
      subtitle: null,
    };
  }, []);

  return (
    <Modal
      width={"1312px"}
      height={"772px"}
      isOpen={true}
      onClose={onClose}
      dsVersion={"2.0"}
      zIndex={1000}
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Header
        title={t("ai:template_library")}
        isClosable={true}
        hasSeparator={true}
      />

      <Modal.Body isScrollable={false} style={styles.modalBodyStyle}>
        {isLoading && !isData ? (
          <Loading />
        ) : (
          <div className={classes.bodyContainer}>
            <SideNavigation
              dsVersion={"2.0"}
              options={options}
              theme="light"
              selectedOptionKey={selectedSidebarItem}
              onClick={onClickSideNavigationOption}
              containerStyle={styles.sideNavigationStyle}
            />

            {!_.isEmpty(childPrompts) ? (
              <div
                className={classes.promptsContainer}
                ref={promptsContainerRef}
              >
                {label ? (
                  <div className={classes.headerLabel}>{label}</div>
                ) : null}

                <PromptCards
                  prompts={childPrompts}
                  allPrompts={flattenedPrompts}
                  selectedPrompt={selectedPrompt}
                  setSelectedPrompt={setSelectedPromptLocal}
                  setSelectedAction={setSelectedChildPrompt}
                  leId={leId}
                  assessmentDetails={assessmentDetails}
                  gradeId={gradeId}
                  assessmentTemplate={assessmentTemplate}
                  source={source}
                  test={true}
                />
              </div>
            ) : null}

            <div className={classes.chatContainer}>
              <AITutorChatWrapper
                containerStyle={styles.aiTutorChatContainerStyle}
                leId={leId}
                entityId={leId}
                assessmentDetails={assessmentDetails}
                course={course}
                emptyStateProps={emptyStateProps}
                showEmptyState={showEmptyState}
                shouldGeneratePreviewChat={shouldGeneratePreviewChat}
                selectedPrompt={selectedPrompt}
                source={"LIBRARY"}
                learningCourseId={learningCourseId}
              />
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer hasSeparator={true}>
        <div className={classes.footerContainer}>
          <Button
            variant={"neutral"}
            type={"outlined"}
            size="medium"
            dsVersion={"2.0"}
            onClick={onClose}
          >
            {t("common:cancel")}
          </Button>

          <Button
            variant={"primary"}
            type={"fill"}
            size="medium"
            dsVersion={"2.0"}
            onClick={onClickUseThisTemplate}
            disabled={_.isEmpty(selectedPrompt)}
          >
            {t("ai:use_this_template")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentDetails } = ownProps;
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);

  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id");

  const aiConversation = _.get(assessmentDetails, "aiConversation.edges", []);

  return {
    userId,
    aiConversation,
    curriculumProgramId,
    curriculumProgramType,
    isData: true,
    isLoading: false,
  };
};

export default compose(
  I18nHOC({ resource: ["common", "ai", "aiTutor"] }),
  connect(mapStateToProps, null),
  graphql(getAIPromptsQuery, {
    name: "getAIPrompts",
    skip: ({ leId, entityType }) => !leId || !entityType,
    options: ({
      leId,
      entityType,
      portalType,
      curriculumProgramId,
      curriculumProgramType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          filters: {
            entityId: leId,
            entityType,
            curriculumProgramId,
            categories: [
              {
                entityType: "CUSTOM",
                entities: curriculumProgramType,
              },
            ],
          },
          portalType,
        },
      };
    },
    props({
      getAIPrompts,
      ownProps: { isData, isLoading, t, i18n, ...props },
    }) {
      const queryData = _.get(getAIPrompts, "ai", {});

      const prompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getAIPrompts, "networkStatus");

      const { flattenedPrompts } = getPromptsForSuggestionLibrary({
        ...props,
        prompts,
      });

      const displayPrompts = _.filter(flattenedPrompts, {
        promptType: "display",
      });

      const options = getAITutorSidebarOptions({
        t,
        i18n,
        displayPrompts,
      });

      const initialOption = _.find(options, ({ key }) => key !== "root")?.key;

      return {
        prompts,
        options,
        initialOption,
        displayPrompts,
        flattenedPrompts,
        isData: isData && !_.isEmpty(queryData),
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(AITutorLibrary);
